import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * UserAvatar Component
 * Displays user profile picture or fallback to initials
 */
const UserAvatar = ({ 
  user, 
  size = 'md', 
  showName = false,
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(
    user?.profile_picture?.startsWith('data:') || false
  );
  
  // Reset error state when user or profile picture changes
  useEffect(() => {
    setImageError(false);
    setImageLoaded(user?.profile_picture?.startsWith('data:') || false);
  }, [user?.id, user?.profile_picture]);
  
  // Size mappings
  const sizeClasses = {
    xs: 'w-5 h-5 text-[10px]',
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
    xl: 'w-12 h-12 text-lg'
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getProfilePictureUrl = (profilePicture) => {
    if (!profilePicture) return null;
    // If it's already a full URL, use it as-is
    if (profilePicture.startsWith('data:')) return profilePicture;
    if (profilePicture.startsWith('http')) return profilePicture;
    // Otherwise, prepend the API base URL
    return `${API}${profilePicture}`;
  };

  const profilePictureUrl = user?.profile_picture && !imageError 
    ? getProfilePictureUrl(user.profile_picture) 
    : null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {profilePictureUrl ? (
        <>
          <img 
            src={profilePictureUrl}
            alt={user?.full_name || 'User'}
            className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0 border border-slate-200 ${!imageLoaded ? 'hidden' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              console.warn('Profile image failed to load:', profilePictureUrl);
              setImageError(true);
            }}
          />
          {/* Show initials while loading or if image fails */}
          {!imageLoaded && (
            <div 
              className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0`}
            >
              {getInitials(user?.full_name)}
            </div>
          )}
        </>
      ) : (
        <div 
          className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0`}
        >
          {getInitials(user?.full_name)}
        </div>
      )}
      {showName && user?.full_name && (
        <span className="text-sm text-slate-700 truncate">{user.full_name}</span>
      )}
    </div>
  );
};

export default UserAvatar;
